import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AssetUploadResult } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { uploadAssetFile, uploadManualAssetFile } from "../../../api/client.js";
import type { AssetScope } from "./useAssetLibrary.js";

/**
 * Streaming asset upload (ADR-025 A2 + addendum #217 P2). Wraps the raw multipart POST (on the auth seam) in a mutation,
 * routed by SCOPE: **master** → `uploadAssetFile` (the realm pool); **manual** → `uploadManualAssetFile` (the manual's own
 * folder, lazy-created server-side on first upload). On success it invalidates the asset-browse query so the drawer refetches
 * — but the file surfaces only once the WORKER flips it `ready` (the browse gates on `ready`), so a refresh may be needed
 * shortly (no polling; the WS "ready" push is Epic B). Uploads into the currently-browsed `folderId` (null ⇒ the scope root).
 */
export function useUploadAsset(session: AuthSession | null, scope: AssetScope, folderId: number | null) {
  const qc = useQueryClient();
  return useMutation<AssetUploadResult, Error, File>({
    mutationFn: (file: File) => (scope.kind === "manual" ? uploadManualAssetFile(scope.manualId, file, folderId) : uploadAssetFile(file, folderId)),
    onSuccess: () => {
      // Prefix match → refetch every scope/folder/type variation of this realm+user's asset browse.
      void qc.invalidateQueries({ queryKey: ["asset-folder", session?.realm, session?.sub] });
    },
  });
}
